import { Component, Input, Output, EventEmitter, OnChanges } from '@angular/core';
import { SubSink } from 'subsink';
import { CoreCommonService } from '../../providers/services/common.service';
import { CoreHttpService } from '../../utilities/services/http.service';
import { ERRORS } from '../../models/constants';
import { throwError } from 'rxjs';
import { FormGroup } from '@angular/forms';
import { CoreUtilityService } from '../../utilities/services/utility.service';

@Component({
  selector: 'app-input-control-read-only',
  templateUrl: './input-control-read-only.component.html'
})
export class InputControlReadOnlyComponent implements OnChanges {
  @Input() fieldControl;
  @Input() form;
  @Input() type;
  @Input() isBundle?: boolean;

  @Input() dynamicControlScope: any;
  @Input() parentScope: any;
  @Output() controlReady = new EventEmitter<boolean>();
  private eventSubscriptionsList: SubSink;

  constructor(private coreCommonService: CoreCommonService,
    private coreHttpService: CoreHttpService,
    private coreUtilityService: CoreUtilityService) {}

  /**
   * A callback method that is invoked if any data-bound properties change
   */
  ngOnChanges() {
    this.eventSubscriptionsList = new SubSink();
    this.handleControlLoad();
  }

  /**
   * This method will handle control load
   * NOTE: Sequence of function calls need to be exactly same as below
   */
  private async handleControlLoad() {
    await this.registerEvents();
    this.notifyControlLoadComplete();
  }

  /**
   * This method will notify when control is ready
   */
  private notifyControlLoadComplete() {
    this.controlReady.emit(true);
  }

  /**
   * getter function to check mandatory fields
   */
  get isMandatory(): boolean {
    try {
      return this.fieldControl.validations.mandatory.value;
    } catch (error) {
      return false;
    }
  }

  /**
   * This method will register the events
   */
  private async registerEvents() {
    try {
      const valueChange = this.registerEmitterValueChange(this.fieldControl, this.form);
      const eventEmitter = this.registerChangeEvent(this.fieldControl, this.form);
      await Promise.all([valueChange, eventEmitter]);
      return true;
    } catch (error) {
      console.log(error);
    }
  }

  /**
   * This method will register change emitter event
   */
  private registerEmitterValueChange(field: object, form: FormGroup) {
    try {
      const changeEmitterFields = field['changeEmitterFields'] || [];
      for (const changeEmitterField of changeEmitterFields) {
        // INFO: Do not add distinctUntilChanged filter since it will block populate chained dependent dropdown's
        this.eventSubscriptionsList.sink = form.get(changeEmitterField['targetFieldLogicalName']).valueChanges.subscribe((value) => {
          this.callFunctionByName(changeEmitterField.functionName, field, value, form, changeEmitterField.params);
        });
      }
      return true;
    } catch (error) {
      throwError(error);
    }
  }

  /**
   * This method will register value changes
   */
  private registerChangeEvent(field: object, form: FormGroup) {
    try {
      if (!field.hasOwnProperty('onChangeEvent') || field['onChangeEvent'].length === 0) {
        return;
      }

      for (const eventSchema of field['onChangeEvent']) {
        this.eventSubscriptionsList.sink = form.get(field['logicalName']).valueChanges.subscribe((value) => {
          this.callFunctionByName(eventSchema.functionName, field, value, form, eventSchema.params);
        });
      }
      return true;
    } catch (error) {
      throw error;
    }
  }

  /**
   * This method will call the schema functions by name
   */
  async callFunctionByName(fnName: string, ...params: any) {
    try {
      let scope: any;
      if (this[fnName]) {
        scope = this;
      } else if (this.dynamicControlScope[fnName]) {
        scope = this.dynamicControlScope;
      } else if (this.parentScope[fnName]) {
        scope = this.parentScope;
      } else {
        throw new Error(`${ERRORS.FUNCTION_NOT_FOUND}: ${fnName}`);
      }
      params ? scope[fnName](...params) : scope[fnName]();
    } catch (error) {
      throwError(`${ERRORS.FUNCTION_CALL_FAILED}: ${error}`);
    }
    return true;
  }

  /**
   * This method will listen for any update in date field
   */
  private async updateListenerDateField(targetField: object, selectedValue: any, form: FormGroup, params?: any[]) {
    try {
      let data: any;
      if (targetField['defaultValueOptionSet'] && targetField['defaultValueOptionSet'].length !== 0) {
        data = targetField['defaultValueOptionSet'];
      } else {
        const api = this.coreCommonService.extractApiAsPerDomain(targetField['optionsListApi']);
        const constructedParam = this.coreCommonService.extractAndConstructParams(api?.params, form);

        data = await this.coreHttpService
          .doCheck(api['url'], api?.type, constructedParam)
          .toPromise()
          .then((val) => val)
          .catch((err) => throwError(err));
      }
      if (data?.ExpectedDate && typeof data?.ExpectedDate === 'object' && data?.ExpectedDate?.year) {
        this.form.get(this.fieldControl?.logicalName).setValue(this.coreUtilityService.convertJSONInDate(data?.ExpectedDate));
      } else if (data?.total) {
        this.form.get(this.fieldControl?.logicalName).setValue(data?.total);
      } else {
        this.form.get(this.fieldControl?.logicalName).setValue(null);
        this.fieldControl.validations.mandatory.message = data?.ExpectedDate;
        this.form.get(this.fieldControl.logicalName).markAsTouched({ onlySelf: true });
        this.form.get(this.fieldControl.logicalName).setErrors({ required: true });
        this.form.get(this.fieldControl.logicalName).setErrors({ invalid: true });
      }
    } catch (error) {
      console.log(ERRORS.ERROR_OCCURED, ' : ', error);
    }
    return true;
  }
}
